import React, { useState, useMemo } from "react";
import CustomTable from "components/Table/CustomTable";
import { useModal } from "contexts/ModalProvider";
import { useToasts } from "utils/useHotToast";
import check from "assets/icons/check-circle.svg";
import slash from "assets/icons/slash-circle.svg";
import dayjs from "dayjs";
import Image from "components/Common/Image";
import { getImageUrl, ImageSourceType } from "utils/imageUtils";
import { useNavigate } from "react-router-dom";

// Custom Hooks
import useTableChangeHandler from "hooks/useTableChangeHandler";
import ToastNotification from "components/Toast/ToastNotification";

import { formatCurrency } from "utils/formatters";
import { NomaroiProductsSchema } from "model/products/nomaroi.schema";
import { useAddProductMutation } from "services/products/api";

const NomaroiProductsTable = ({ data, onTableChange, error, isLoading }) => {
  const navigate = useNavigate();
  const { modal } = useModal();
  const { handleSort, handleFilter, handleSearch, handlePageChange } = useTableChangeHandler(onTableChange);
  const { addToast, removeToast } = useToasts();
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [addProduct, { isLoading: isAddingProduct }] = useAddProductMutation();

  const columns = useMemo(
    () => [
      {
        header: "รายการ Product Affiliate",
        accessor: "name",
        sortable: true,
        render: (item) => (
          <div className="product-info d-flex align-items-center gap-3">
            <Image src={getImageUrl(item.imgThumbnail, { type: ImageSourceType.NOMAROI })} alt={item.name} />
            <div>
              <div className="text-lg fw-semibold">{item.name}</div>
              <div className="text-md text-gray-500">SKU: {item.sku}</div>
            </div>
          </div>
        ),
      },
      {
        header: "แบรนด์ F&N",
        accessor: "brand",
        responsive: "md",
        render: (item) => (
          <div className="brand-info d-flex align-items-center gap-3">
            <Image src={getImageUrl(item.brand?.imgThumbnail, { type: ImageSourceType.NOMAROI })} alt={item.brand?.name} />
            <div className="text-lg">{item.brand?.name}</div>
          </div>
        ),
      },
      {
        header: "หมวดหมู่สินค้า",
        accessor: "category",
        responsive: "lg",
        render: (item) => <div className="text-success-500 fw-semibold">{item.category?.name}</div>,
      },
      {
        header: "ราคา",
        accessor: "price",
        sortable: true,
        align: "end",
        render: (item) => formatCurrency(item.price),
      },
      {
        header: "วันที่สร้างสินค้า",
        accessor: "createdDate",
        responsive: "xl",
        render: (item) => dayjs(item.createdAt).format("YYYY-MM-DD"),
      },
      {
        header: "",
        accessor: "actions",
        width: 50,
        render: (item) => (
          <div
            className="text-gray-500 cursor-pointer"
            onClick={(event) => {
              event.stopPropagation();
              handleAdd(item);
            }}
          >
            <i className="bx bx-plus"></i>
          </div>
        ),
      },
    ],
    []
  );

  const handleAdd = async (item) => {
    const result = await modal.alert.success({
      title: "ยืนยันที่จะนำสินค้า Affiliate เข้าสู่ระบบ",
      message: `ตรวจสอบรายละเอียดรายการสินค้าที่จะนำสินค้า Affiliate เข้าสู่ระบบ`,
      confirmText: "ยืนยันที่จะนำเข้า",
      cancelText: "ยกเลิก",
      children: (
        <div className="d-flex flex-column gap-2">
          <div className="product-info d-flex align-items-center gap-3">
            <Image src={getImageUrl(item.imgThumbnail, { type: ImageSourceType.NOMAROI })} alt={item.name} />
            <div>
              <div className="text-lg fw-semibold">{item.name}</div>
              <div className="text-md text-gray-500">SKU: {item.id}</div>
            </div>
          </div>
        </div>
      ),
    });
    if (result) {
      try {
        await addProduct(item);
        const toastId = addToast(
          <ToastNotification
            //
            icon={check}
            title="เพิ่มสินค้าสำเร็จ"
            description={`เพิ่มสินค้า ${item.name} ไปยัง Affiliate เรียบร้อยแล้ว`}
            primaryButtonText="ดูสินค้า Affiliate"
            onPrimaryClick={() => removeToast(toastId)}
            onSecondaryClick={() => removeToast(toastId)}
            variant="success"
          />,
          {
            autoDismiss: true,
          }
        );
        // Refresh data
        if (onTableChange) {
          onTableChange({});
        }
      } catch (error) {
        console.error("Failed to add product to affiliate:", error);
        const toastId = addToast(<ToastNotification icon={slash} title="เกิดข้อผิดพลาด" description="ไม่สามารถเพิ่มสินค้าไปยัง Affiliate ได้ กรุณาลองใหม่อีกครั้ง" onSecondaryClick={() => removeToast(toastId)} variant="danger" />, {
          autoDismiss: true,
        });
      }
    }
  };

  const handleAddAll = async (items) => {
    const result = await modal.alert.success({
      title: "ยืนยันที่จะนำสินค้า Affiliate เข้าสู่ระบบ",
      message: `คุณได้ทำการกดเพิ่มรายการสินค้า Affiliate ที่มีอยู่ในระบบทั้งหมด “${items.length} รายการ”`,
      confirmText: "ยืนยันที่จะนำเข้า",
      cancelText: "ยกเลิก",
      children: (
        <div className="d-flex flex-column gap-3 divide-y">
          {items.map((item, index) => (
            <div key={item.productId} className={`d-flex flex-column gap-2 ${index > 0 && "pt-3"}`}>
              <div className="product-info d-flex align-items-center gap-3">
                <Image src={getImageUrl(item.imgThumbnail, { type: ImageSourceType.NOMAROI })} alt={item.name} />
                <div>
                  <div className="text-lg fw-semibold">{item.name}</div>
                  <div className="text-md text-gray-500">SKU: {item.id}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      ),
    });
    if (result) {
      try {
        await addProduct(items);
        const toastId = addToast(
          <ToastNotification
            //
            variant="success"
            icon={check}
            title="เพิ่มสินค้าสำเร็จ"
            description={`เพิ่มสินค้า Affiliate เข้าสู่ระแบบแล้ว ${items.length} รายการ`}
            primaryButtonText="ดูสินค้า Affiliate"
            onPrimaryClick={() => {
              removeToast(toastId);
              navigate("/products?tab=affiliate");
            }}
            onSecondaryClick={() => removeToast(toastId)}
          />,
          {
            autoDismiss: true,
          }
        );
        // Refresh data
        if (onTableChange) {
          onTableChange({});
        }
      } catch (error) {
        console.error("Failed to add products to affiliate:", error);
        const toastId = addToast(<ToastNotification variant="danger" icon={slash} title="เกิดข้อผิดพลาด" description="ไม่สามารถเพิ่มสินค้าไปยัง Affiliate ได้ กรุณาลองใหม่อีกครั้ง" onSecondaryClick={() => removeToast(toastId)} />, {
          autoDismiss: true,
        });
      }
    }
  };

  const handleSelectionChange = (selected) => {
    setSelectedProducts(selected);
  };

  return (
    <div>
      <div className="text-lg mb-4">รายการ Product ที่ถูกเพิ่มแล้วในระบบทั้งหมด *{data?.pagination?.total?.toLocaleString() || 0} รายการ *</div>

      <CustomTable
        keyField="id"
        data={data?.data || []}
        columns={columns}
        selectable={true}
        onSelectionChange={handleSelectionChange}
        selectionActions={
          <a
            className="success"
            onClick={() => {
              if (selectedProducts.length > 0) {
                handleAddAll(selectedProducts);
              }
            }}
          >
            <i className="bx bx-plus" />
            <span className="d-none d-md-block">เพิ่มรายการที่เลือกไว้</span>
          </a>
        }
        // onRowClick={item => console.log("Row clicked:", item)}
        className="nomaroi-table"
        itemsPerPage={data?.pagination?.limit || 10}
        totalItems={data?.pagination?.total || 0}
        onSort={handleSort}
        onFilter={handleFilter}
        onSearch={handleSearch}
        onPageChange={handlePageChange}
        status={isLoading ? "loading" : error ? "error" : data?.data?.length === 0 ? "empty" : "normal"}
        errorMessage={error?.message || "เกิดข้อผิดพลาดในการโหลดข้อมูล"}
        emptyMessage="ไม่พบข้อมูลสินค้าจาก NomAroi"
      />
    </div>
  );
};

const withYupValidation = (WrappedComponent, schema) => {
  return function WithYupValidation(props) {
    try {
      if (!props.isLoading && !props.error && props.data) {
        schema.validateSync(props.data);
      }
      return <WrappedComponent {...props} />;
    } catch (error) {
      return (
        <div className="alert alert-danger">
          <strong>Validation Error:</strong> {error.message}
        </div>
      );
    }
  };
};

const ValidatedTable = withYupValidation(NomaroiProductsTable, NomaroiProductsSchema);

export default ValidatedTable;
